import { useState, useEffect } from 'react';

import { Link, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { NavLink as RouterLink } from 'react-router-dom';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { useStyles } from './style';
import { Table, EmptyState } from '../../../../components';
import { commaTimeStrings } from '../../../../shared/datetime';
import { usePagination } from '../../../../shared/hooks';
import { isObject, isEmptyArray, capitalizeFirstCharacter } from '../../../../shared/utility';
import * as actions from '../../../../store/actions';

interface CommandActivity {
	id?: number | string;
	type?: string;
	timestamp?: string;
	user?: {
		id?: number | string;
		name?: string;
		organisationReference?: {
			id?: number | string;
			name?: string;
		};
	};
	itemInstance?: {
		name?: string; // comes empty - workaround made to take it from parent container
		itemReference?: {
			id?: number | string;
			name?: string;
		};
	};
}

interface CommandActivitiesData {
	results?: CommandActivity[];
	total?: number;
}

interface InstanceDetailsLogsProps {
	itemId?: string;
	itemInstanceId?: string;
	itemInstanceName?: string;
	onFetchItemInstanceCommandActivities?(...args: unknown[]): unknown;
	itemInstanceCommandActivities?: {
		data?: CommandActivitiesData;
		loading?: boolean;
		error?: object | string;
	};
}

const InstanceDetailsLogs = (props: InstanceDetailsLogsProps) => {
	const {
		itemId,
		itemInstanceId,
		itemInstanceName,
		onFetchItemInstanceCommandActivities,
		itemInstanceCommandActivities,
	} = props;
	const { t } = useTranslation();
	const classes = useStyles();

	const [shouldDoInitialFetch, setShouldDoInitialFetch] = useState(true);
	const [showingInitialResults, setShowingInitialResults] = useState(true);

	const pagination = usePagination(
		'pageNumberInstanceCommandActivities',
		'pageSizeInstanceCommandActivities'
	);
	const { fetch, page, pageNumber, pageSize, pageNumberChange, pageSizeChange, setFatch } =
		pagination;

	useEffect(() => {
		if (!itemId || !itemInstanceId || !onFetchItemInstanceCommandActivities) {
			return;
		}

		if (shouldDoInitialFetch || fetch) {
			if (showingInitialResults) {
				setShowingInitialResults(false);
			}
			onFetchItemInstanceCommandActivities(itemId, itemInstanceId, page);
		}

		if (shouldDoInitialFetch) {
			setShouldDoInitialFetch(false);
		} else if (fetch) {
			setFatch(false);
		}
	}, [
		itemId,
		itemInstanceId,
		onFetchItemInstanceCommandActivities,
		fetch,
		page,
		setFatch,
		shouldDoInitialFetch,
		showingInitialResults,
	]);

	const handleLink = (path?: string, label?: string) =>
		label && path ?
			<Link
				className={classes.itemGroup}
				color='primary'
				component={RouterLink}
				to={path}
				state={{ from: location.pathname, label: t('ui.activity') }}
			>
				{label}
			</Link>
		:	(label ?? '-');

	const {
		data: activitiesData,
		loading: activitiesLoading,
		error: activitiesError,
	} = itemInstanceCommandActivities || {};
	const activitiesReady = isObject(activitiesData) && !activitiesLoading && !activitiesError;

	const getActivityLabel = (activity: CommandActivity) => {
		const label = activity?.type;
		return label ? processActivityLabel(label) : '-';
	};

	const processActivityLabel = (label: string) => {
		switch (label) {
			case 'enable':
				return t('ui.device.label.enableIgnition');
			case 'lock':
				return t('lock');
			case 'unlock':
				return t('unlock');
			case 'reboot':
				return t('reset');
			case 'disable':
				return t('ui.device.label.allowStartEngine');
			default:
				return capitalizeFirstCharacter(String(label));
		}
	};

	const getActivityTime = (activity: CommandActivity) => {
		const timestamp = activity?.timestamp;
		return timestamp ? commaTimeStrings(timestamp) : '-';
	};

	const getItemLabels = (itemId: string, itemName: string, itemInstanceName: string) => {
		const itemPath = itemId ? `/item-management/items/${itemId}/summary` : undefined;
		return (
			<>
				<Typography>{handleLink(itemPath, itemName)}</Typography>
				<Typography variant='body1' color='text.secondary'>
					{itemInstanceName}
				</Typography>
			</>
		);
	};

	const tableHeaders = [
		{ name: 'time', content: t('timestamp') },
		{ name: 'item', content: t('ui.label.item') },
		{ name: 'user', content: t('views.ItemDetail.activities.user') },
		{ name: 'organisation', content: t('ui.organisation') },
		{ name: 'command', content: t('ui.command') },
	];

	const activityResults =
		activitiesReady && Array.isArray(activitiesData?.results) ? activitiesData.results : [];

	const tableBody =
		activitiesReady && !activitiesLoading ?
			activityResults.map((item: CommandActivity) => {
				const userId = item?.user?.id;
				const userName = item?.user?.name;
				const userPath = userId ? `/user-management/users/${userId}/summary` : undefined;
				const organisationId = item?.user?.organisationReference?.id;
				const organisationName = item?.user?.organisationReference?.name;
				const organisationPath =
					organisationId ? `/organisations/${organisationId}/summary` : undefined;
				const itemName = item?.itemInstance?.itemReference?.name;
				const itemId = item?.itemInstance?.itemReference?.id;

				return [
					{ content: getActivityTime(item) },
					{
						content: getItemLabels(
							itemId ? itemId.toString() : '',
							itemName ?? '',
							itemInstanceName ?? ''
						),
					},
					{
						content: handleLink(userPath, userName),
					},
					{
						content: handleLink(organisationPath, organisationName),
					},
					{ content: getActivityLabel(item) },
				];
			})
		:	Array(4)
				.fill(Array(tableHeaders.length).fill(undefined))
				.map((arr) => arr.map(() => ({ loading: true })));

	/* * * * * * * *
	 * EMPTY STATE *
	 * * * * * * * */
	const itemActivityEmpty =
		isObject(activitiesData) && !activitiesLoading && isEmptyArray(activitiesData?.results) ?
			<EmptyState
				image={'item' as unknown as unknown[]}
				subTitle={t(
					showingInitialResults ?
						'views.itemDetail.activities.table.empty.description.zeroInSystem'
					:	'views.itemDetail.activities.table.empty.description.zeroMatching'
				)}
				title={t('views.itemDetail.activities.table.empty.title')}
			/>
		:	null;

	return (
		<div className={classes.root}>
			{itemActivityEmpty ?
				itemActivityEmpty
			:	<Table
					body={tableBody}
					cellStyle={classes.cellStyle}
					handlePageChange={pageNumberChange}
					header={tableHeaders}
					loading={activitiesLoading}
					page={pageNumber}
					rowsPerPage={pageSize}
					setRowsPerPage={pageSizeChange}
					title={t('logs')}
					total={activitiesData?.total ?? 0}
				/>
			}
		</div>
	);
};

const mapStateToProps = (state: {
	paged: {
		itemInstanceCommandActivities: InstanceDetailsLogsProps['itemInstanceCommandActivities'];
	};
}) => {
	return {
		itemInstanceCommandActivities: state.paged.itemInstanceCommandActivities,
	};
};

const mapDispatchToProps = (dispatch: ThunkDispatch<unknown, unknown, AnyAction>) => {
	const fetchItemInstanceCommandActivities =
		actions.fetchItemInstanceCommandActivities as unknown as (
			itemId: string,
			itemInstanceId: string,
			page: { number: number; size: number }
		) => AnyAction;

	return {
		onFetchItemInstanceCommandActivities: (
			itemId: string,
			itemInstanceId: string,
			page: { number: number; size: number }
		) => dispatch(fetchItemInstanceCommandActivities(itemId, itemInstanceId, page)),
	};
};

export default connect(mapStateToProps, mapDispatchToProps)(InstanceDetailsLogs);
